/** Reuses viewport animations inspired by Animate.css presets (Back Entrances, Fading Entrances, Lightspeed). */
import { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useMobileLightMotion } from './useMobileLightMotion';

export type AnimatePreset =
  // Back entrances
  | 'backInUp'
  | 'backInDown'
  | 'backInLeft'
  | 'backInRight'
  // Fading entrances
  | 'fadeIn'
  | 'fadeInUp'
  | 'fadeInUpBig'
  | 'fadeInDown'
  | 'fadeInDownBig'
  | 'fadeInLeft'
  | 'fadeInLeftBig'
  | 'fadeInRight'
  | 'fadeInRightBig'
  | 'fadeInTopLeft'
  | 'fadeInTopRight'
  | 'fadeInBottomLeft'
  | 'fadeInBottomRight'
  // Lightspeed
  | 'lightSpeedInRight'
  | 'lightSpeedInLeft'
  // Legacy Aliases
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'fadeBottomUp'
  | 'fadeDownLeft'
  | 'fadeDownRight'
  | 'fadeTopDown'
  | 'fadeFromDownLeft'
  | 'fadeFromDownRight';

interface RevealProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly delay?: number;
  readonly direction?: AnimatePreset;
  readonly distance?: number;
  readonly duration?: number;
  readonly once?: boolean;
}

function getPresetConfig(preset: AnimatePreset = 'fadeInUp', distance = 48) {
  switch (preset) {
    // Back Entrances (Scale down + sweeping distance offset -> Smoothly zooms back into place)
    case 'backInUp':
      return {
        initial: { opacity: 0, y: distance * 1.8, scale: 0.78 },
        animate: { opacity: 1, y: 0, scale: 1 },
        transition: { type: 'spring' as const, stiffness: 110, damping: 16, mass: 0.9 },
      };
    case 'backInDown':
      return {
        initial: { opacity: 0, y: -distance * 1.8, scale: 0.78 },
        animate: { opacity: 1, y: 0, scale: 1 },
        transition: { type: 'spring' as const, stiffness: 110, damping: 16, mass: 0.9 },
      };
    case 'backInLeft':
      return {
        initial: { opacity: 0, x: -distance * 1.8, scale: 0.78 },
        animate: { opacity: 1, x: 0, scale: 1 },
        transition: { type: 'spring' as const, stiffness: 110, damping: 16, mass: 0.9 },
      };
    case 'backInRight':
      return {
        initial: { opacity: 0, x: distance * 1.8, scale: 0.78 },
        animate: { opacity: 1, x: 0, scale: 1 },
        transition: { type: 'spring' as const, stiffness: 110, damping: 16, mass: 0.9 },
      };

    // Lightspeed Entrances (Dramatic translate + skewX -> Gracefully snaps into place)
    case 'lightSpeedInRight':
      return {
        initial: { opacity: 0, x: distance * 2.8, skewX: -22 },
        animate: { opacity: 1, x: 0, skewX: 0 },
        transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'lightSpeedInLeft':
      return {
        initial: { opacity: 0, x: -distance * 2.8, skewX: 22 },
        animate: { opacity: 1, x: 0, skewX: 0 },
        transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1] as const },
      };

    // Fading Entrances (Lush 0.95s smooth fade & slide)
    case 'fadeIn':
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.9, ease: 'easeOut' as const },
      };
    case 'fadeInUp':
    case 'fadeBottomUp':
    case 'up':
      return {
        initial: { opacity: 0, y: distance },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInUpBig':
      return {
        initial: { opacity: 0, y: distance * 2.5 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInDown':
    case 'fadeTopDown':
    case 'down':
      return {
        initial: { opacity: 0, y: -distance },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInLeft':
    case 'left':
      return {
        initial: { opacity: 0, x: -distance },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInLeftBig':
      return {
        initial: { opacity: 0, x: -distance * 2.8 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInRight':
    case 'right':
      return {
        initial: { opacity: 0, x: distance },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInRightBig':
      return {
        initial: { opacity: 0, x: distance * 2.8 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInTopLeft':
      return {
        initial: { opacity: 0, x: -distance, y: -distance },
        animate: { opacity: 1, x: 0, y: 0 },
        transition: { duration: 0.98, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInTopRight':
    case 'fadeDownLeft':
      return {
        initial: { opacity: 0, x: distance, y: -distance },
        animate: { opacity: 1, x: 0, y: 0 },
        transition: { duration: 0.98, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInBottomLeft':
    case 'fadeFromDownLeft':
      return {
        initial: { opacity: 0, x: -distance, y: distance },
        animate: { opacity: 1, x: 0, y: 0 },
        transition: { duration: 0.98, ease: [0.16, 1, 0.3, 1] as const },
      };
    case 'fadeInBottomRight':
    case 'fadeDownRight':
      return {
        initial: { opacity: 0, x: distance, y: distance },
        animate: { opacity: 1, x: 0, y: 0 },
        transition: { duration: 0.98, ease: [0.16, 1, 0.3, 1] as const },
      };

    default:
      return {
        initial: { opacity: 0, y: distance },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] as const },
      };
  }
}

export function Reveal({
  children,
  className,
  delay = 0.05,
  direction = 'fadeInUp',
  distance = 44,
  duration,
  once = true,
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const useLightMotion = useMobileLightMotion();
  const isInView = useInView(ref, {
    once,
    amount: 0.1,
    margin: '0px 0px -30px 0px',
  });
  const config = getPresetConfig(direction, distance);
  const motionConfig = useLightMotion
    ? {
        initial: { opacity: 0, y: Math.min(distance, 18) },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.32, ease: 'easeOut' as const },
      }
    : config;

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduceMotion ? false : motionConfig.initial}
      animate={reduceMotion ? motionConfig.animate : isInView ? motionConfig.animate : motionConfig.initial}
      transition={{
        delay: reduceMotion || useLightMotion ? 0 : delay,
        ...(duration && !useLightMotion ? { duration } : motionConfig.transition),
      }}
    >
      {children}
    </motion.div>
  );
}
